import { useState, useMemo } from 'react';

const DIMENSIONS = [
  { key: 'city',       label: 'City',       getVal: p => p.city || '',               isArray: false },
  { key: 'language',   label: 'Language',   getVal: p => p.home_language || [],      isArray: true  },
  { key: 'sect',       label: 'Sect',       getVal: p => p.sect || '',               isArray: false },
  { key: 'diet',       label: 'Diet',       getVal: p => p.diet_practice || '',      isArray: false },
  { key: 'practice',   label: 'Practice',   getVal: p => p.practice_level || '',     isArray: false },
  { key: 'profession', label: 'Profession', getVal: p => p.profession || '',         isArray: false },
  { key: 'music',      label: 'Music',      getVal: p => p.fav_song || '',           isArray: false },
  { key: 'shows',      label: 'Shows',      getVal: p => p.fav_show || '',           isArray: false },
  { key: 'vibe',       label: 'Vibe',       getVal: p => p.personality_traits || [], isArray: true  },
  { key: 'interests',  label: 'Interests',  getVal: p => p.hobbies_list || [],       isArray: true  },
];

const TILES = [
  { key: 'aligned',   label: 'Aligned'   },
  { key: 'different', label: 'Different' },
  { key: 'findout',   label: 'Find out'  },
];

function isFilled(val) {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim() !== '';
  if (Array.isArray(val)) return val.length > 0;
  return Boolean(val);
}

function computeState(myVal, theirVal, isArray) {
  if (!isFilled(myVal) || !isFilled(theirVal)) return 'findout';
  if (isArray) {
    const a = Array.isArray(myVal) ? myVal : [];
    const b = Array.isArray(theirVal) ? theirVal : [];
    return a.some(x => b.includes(x)) ? 'aligned' : 'different';
  }
  return String(myVal) === String(theirVal) ? 'aligned' : 'different';
}

function getOverlap(a, b) {
  return (Array.isArray(a) ? a : []).filter(x => (Array.isArray(b) ? b : []).includes(x));
}

function shortList(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '—';
  return arr.slice(0, 2).join(', ') + (arr.length > 2 ? '…' : '');
}

export default function CompatibilityTiles({ viewedProfile, myProfile }) {
  const [selectedTile, setSelectedTile] = useState('aligned');

  const dimensions = useMemo(() => {
    if (!viewedProfile || !myProfile) return [];
    return DIMENSIONS.map(dim => {
      const myVal = dim.getVal(myProfile);
      const theirVal = dim.getVal(viewedProfile);
      return { ...dim, myVal, theirVal, state: computeState(myVal, theirVal, dim.isArray) };
    });
  }, [myProfile, viewedProfile]);

  if (!myProfile || viewedProfile.id === myProfile.id) return null;

  const counts = {
    aligned:   dimensions.filter(d => d.state === 'aligned').length,
    different: dimensions.filter(d => d.state === 'different').length,
    findout:   dimensions.filter(d => d.state === 'findout').length,
  };

  const drill = dimensions.filter(d => d.state === selectedTile);

  return (
    <div style={{ padding: '0 16px 8px' }}>
      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', position: 'relative', zIndex: 1 }}>
        {TILES.map(tile => {
          const active = selectedTile === tile.key;
          return (
            <div key={tile.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
              <button
                onClick={() => setSelectedTile(tile.key)}
                className={active
                  ? 'bg-saffron-mid text-white'
                  : 'bg-card border border-border text-foreground'
                }
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}>
                  {counts[tile.key]}
                </span>
                <span style={{ fontSize: 11, lineHeight: 1.2 }}>{tile.label}</span>
              </button>
              {/* Speech-bubble pointer — rotated square positioned below active tile */}
              <div
                className={active ? 'bg-saffron-mid' : ''}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 2,
                  transform: 'rotate(45deg)',
                  marginTop: -7,
                  visibility: active ? 'visible' : 'hidden',
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Drill-down card */}
      <div
        className="bg-card border border-border"
        style={{ borderRadius: 14, overflow: 'hidden', marginTop: 2 }}
      >
        {drill.length === 0 ? (
          <p
            className="text-muted-foreground"
            style={{ padding: '12px 16px', fontSize: 13, textAlign: 'center' }}
          >
            Nothing here — all good.
          </p>
        ) : (
          drill.map((dim, i) => {
            let rightText;
            if (dim.state === 'aligned') {
              rightText = dim.isArray
                ? shortList(getOverlap(dim.myVal, dim.theirVal))
                : String(dim.myVal);
            } else if (dim.state === 'different') {
              rightText = dim.isArray
                ? `You: ${shortList(dim.myVal)} · Them: ${shortList(dim.theirVal)}`
                : `You: ${dim.myVal} · Them: ${dim.theirVal}`;
            } else {
              rightText = 'Not filled yet';
            }

            return (
              <div key={dim.key}>
                {i > 0 && (
                  <div className="border-t border-border" style={{ margin: '0 16px' }} />
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    gap: 8,
                  }}
                >
                  <span
                    className="text-foreground"
                    style={{ fontSize: 13, fontWeight: 500, flexShrink: 0 }}
                  >
                    {dim.label}
                  </span>
                  <span
                    className={dim.state === 'findout' ? 'text-muted-foreground' : 'text-foreground'}
                    style={{
                      fontSize: 12,
                      maxWidth: '60%',
                      textAlign: 'right',
                      fontStyle: dim.state === 'findout' ? 'italic' : 'normal',
                    }}
                  >
                    {rightText}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
